import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';
import type { CampaignTag, MetaCampaign, GoogleCampaign, ServiceLabel } from '../types';
import { SERVICE_LABELS, SERVICE_SHORT, SERVICE_COLORS } from '../types';

interface AllCampaign {
  campaign_id: string;
  campaign_name: string;
  source: 'meta' | 'google';
  account_id?: string;
  spend: number;
  service_tag: string | null;
}

type SourceFilter = 'all' | 'meta-1' | 'meta-2' | 'google';

const SERVICE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Sem tag' },
  ...SERVICE_LABELS.slice(1).map(s => ({ value: s, label: SERVICE_SHORT[s] })),
];

function getAccountName(accountId: string | undefined, _source: string): string {
  if (!accountId) return '';
  return accountId.replace('act_', '');
}

export default function CampaignTags() {
  const [campaigns, setCampaigns] = useState<AllCampaign[]>([]);
  const [saving, setSaving] = useState<Map<string, 'saving' | 'saved'>>(new Map());
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      apiFetch<CampaignTag[]>('/api/campaigns/tags'),
      apiFetch<MetaCampaign[]>('/api/metrics/campaigns'),
      apiFetch<GoogleCampaign[]>('/api/metrics/google-campaigns'),
    ]).then(([tags, metaCampaigns, googleCampaigns]) => {
      const tagMap = new Map(tags.map(t => [`${t.campaign_id}_${t.source}`, t.service]));

      const all: AllCampaign[] = [
        ...metaCampaigns.map(c => ({
          campaign_id: c.campaign_id,
          campaign_name: c.campaign_name,
          source: 'meta' as const,
          account_id: c.account_id,
          spend: c.spend,
          service_tag: tagMap.get(`${c.campaign_id}_meta`) ?? c.service_tag ?? null,
        })),
        ...googleCampaigns.map(c => ({
          campaign_id: c.campaign_id,
          campaign_name: c.campaign_name,
          source: 'google' as const,
          account_id: c.customer_id,
          spend: c.spend,
          service_tag: tagMap.get(`${c.campaign_id}_google`) ?? c.service_tag ?? null,
        })),
      ];

      const deduped = Array.from(
        all.reduce((map, c) => {
          const key = `${c.campaign_id}_${c.source}`;
          if (!map.has(key) || c.spend > map.get(key)!.spend) map.set(key, c);
          return map;
        }, new Map<string, AllCampaign>()).values()
      ).sort((a, b) => b.spend - a.spend);

      setCampaigns(deduped);
    }).catch(() => {}).finally(() => setIsLoading(false));
  }, []);

  async function handleTagChange(campaign: AllCampaign, newService: string) {
    const key = `${campaign.campaign_id}_${campaign.source}`;
    setSaving(m => new Map(m).set(key, 'saving'));

    try {
      if (newService === '') {
        await apiFetch(`/api/campaigns/tags/${campaign.campaign_id}/${campaign.source}`, { method: 'DELETE' });
      } else {
        await apiFetch('/api/campaigns/tags', {
          method: 'POST',
          body: JSON.stringify({
            campaign_id: campaign.campaign_id,
            source: campaign.source,
            account_id: campaign.account_id,
            campaign_name: campaign.campaign_name,
            service: newService,
          }),
        });
      }
      setCampaigns(prev => prev.map(c =>
        c.campaign_id === campaign.campaign_id && c.source === campaign.source
          ? { ...c, service_tag: newService || null }
          : c
      ));
      setSaving(m => { const n = new Map(m); n.set(key, 'saved'); return n; });
      setTimeout(() => setSaving(m => { const n = new Map(m); n.delete(key); return n; }), 1500);
    } catch {
      setSaving(m => { const n = new Map(m); n.delete(key); return n; });
    }
  }

  const filtered = campaigns.filter(c => {
    if (sourceFilter === 'all') return true;
    if (sourceFilter === 'google') return c.source === 'google';
    return c.source === 'meta';
  });

  const untaggedCount = campaigns.filter(c => !c.service_tag).length;

  return (
    <div style={{ padding: '28px', maxWidth: '1440px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: 'var(--text-primary)' }}>Campaign Tags</h1>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-tertiary)' }}>
            Associe campanhas aos serviços
            {untaggedCount > 0 && (
              <span style={{ color: 'var(--yellow)', marginLeft: '8px' }}>
                ⚠ {untaggedCount} sem tag
              </span>
            )}
          </p>
        </div>
        {/* Source Filter */}
        <div style={{ display: 'flex', gap: '4px' }}>
          {(['all', 'meta-1', 'meta-2', 'google'] as SourceFilter[]).map(f => (
            <button
              key={f}
              onClick={() => setSourceFilter(f)}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: sourceFilter === f ? 'var(--accent-border)' : 'var(--border-subtle)',
                background: sourceFilter === f ? 'var(--accent-muted)' : 'transparent',
                color: sourceFilter === f ? 'var(--accent)' : 'var(--text-tertiary)',
                fontSize: '12px',
                fontFamily: 'var(--font-mono)',
                cursor: 'pointer',
              }}
            >
              {f === 'all' ? 'Todos' : f === 'meta-1' ? 'Meta 1' : f === 'meta-2' ? 'Meta 2' : 'Google'}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <div className="spinner" />
        </div>
      ) : (
        <div style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-subtle)',
          borderRadius: '12px',
          overflow: 'hidden',
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  {['Campanha', 'Fonte', 'Gasto', 'Serviço'].map(h => (
                    <th key={h} style={{
                      padding: '10px 16px',
                      textAlign: h === 'Gasto' ? 'right' : 'left',
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase' as const,
                      color: 'var(--text-tertiary)',
                      fontFamily: 'var(--font-sans)',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => {
                  const key = `${c.campaign_id}_${c.source}`;
                  const savingState = saving.get(key);
                  const isUntagged = !c.service_tag;
                  const serviceColor = c.service_tag ? SERVICE_COLORS[c.service_tag as ServiceLabel] : null;

                  return (
                    <tr
                      key={key}
                      style={{
                        borderBottom: i < filtered.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                        background: isUntagged ? 'rgba(234,179,8,0.03)' : 'transparent',
                      }}
                    >
                      <td style={{ padding: '10px 16px', maxWidth: '280px' }}>
                        <div style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.campaign_name}
                        </div>
                        {c.account_id && (
                          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', marginTop: '2px' }}>
                            {getAccountName(c.account_id, c.source)}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontFamily: 'var(--font-mono)',
                          background: c.source === 'meta' ? 'rgba(59,130,246,0.12)' : 'rgba(34,197,94,0.12)',
                          color: c.source === 'meta' ? 'var(--blue)' : 'var(--green)',
                        }}>
                          {c.source === 'meta' ? 'Meta' : 'Google'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>
                        R$ {c.spend.toFixed(2)}
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <select
                            value={c.service_tag ?? ''}
                            onChange={e => handleTagChange(c, e.target.value)}
                            style={{
                              padding: '5px 10px',
                              background: 'var(--bg-tertiary)',
                              border: `1px solid ${serviceColor ? `${serviceColor}40` : 'var(--border-default)'}`,
                              borderRadius: '6px',
                              color: serviceColor ?? 'var(--text-tertiary)',
                              fontSize: '12px',
                              fontFamily: 'var(--font-mono)',
                              cursor: 'pointer',
                              outline: 'none',
                            }}
                          >
                            {SERVICE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value} style={{ background: '#18181b' }}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          {savingState === 'saving' && <div className="spinner" />}
                          {savingState === 'saved' && <span style={{ color: 'var(--green)', fontSize: '14px' }}>✓</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
